import { posthog } from "posthog-js";
import { ANALYTICS_CONSENT_STORAGE_KEY } from "./consent.js";
import type { AnalyticsEventName } from "./events.js";
import type {
  AnalyticsClient,
  AnalyticsClientDeps,
  AnalyticsConfig,
  AnalyticsEventProperties,
  IdentifyUserInput
} from "./types.js";

const noopClient: AnalyticsClient = {
  trackPageView: () => undefined,
  trackEvent: () => undefined,
  setAnalyticsConsent: () => undefined,
  identifyUser: () => undefined,
  resetUser: () => undefined,
  getDistinctId: () => null,
  aliasDistinctId: () => undefined
};

const defaultIsBrowser = () => typeof globalThis.window !== "undefined";

export const createAnalyticsClient = (
  config: AnalyticsConfig,
  deps: AnalyticsClientDeps = {}
): AnalyticsClient => {
  const backend = deps.posthog ?? posthog;
  const isBrowser = deps.isBrowser ?? defaultIsBrowser;

  if (!config.enabled || config.apiKey.length === 0 || !isBrowser()) {
    return noopClient;
  }

  const requiresConsent = config.surface === "marketing";

  backend.init(config.apiKey, {
    api_host: config.host,
    ui_host: config.uiHost,
    autocapture: false,
    capture_pageview: false,
    disable_session_recording: true,
    persistence: config.surface === "marketing" ? "localStorage+cookie" : "localStorage+cookie",
    cross_subdomain_cookie: config.crossSubdomainCookie === true,
    opt_out_capturing_by_default: requiresConsent
  });

  const capturePageView = (path: string) => {
    backend.capture("$pageview", { $current_url: path });
  };

  const captureEvent = (name: AnalyticsEventName, properties?: AnalyticsEventProperties) => {
    backend.capture(name, properties ?? {});
  };

  const hasConsent = (): boolean => {
    if (!requiresConsent) {
      return true;
    }
    return globalThis.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY) === "granted";
  };

  const setAnalyticsConsent = (granted: boolean) => {
    if (requiresConsent) {
      globalThis.localStorage.setItem(
        ANALYTICS_CONSENT_STORAGE_KEY,
        granted ? "granted" : "denied"
      );
    }
    if (granted) {
      backend.opt_in_capturing();
      capturePageView(globalThis.location.pathname + globalThis.location.search);
    } else {
      backend.opt_out_capturing();
    }
  };

  const identifyUser = (input: IdentifyUserInput) => {
    if (!hasConsent() && requiresConsent) {
      return;
    }
    backend.identify(input.userId, {
      workspace_id: input.workspaceId ?? undefined,
      role: input.role ?? undefined
    });
  };

  const resetUser = () => {
    backend.reset();
  };

  const aliasDistinctId = (userId: string) => {
    if (!hasConsent() && requiresConsent) {
      return;
    }
    backend.alias?.(userId);
  };

  if (requiresConsent && hasConsent()) {
    backend.opt_in_capturing();
  }

  return {
    trackPageView: (path) => {
      if (!hasConsent() && requiresConsent) {
        return;
      }
      capturePageView(path);
    },
    trackEvent: (name, properties) => {
      if (!hasConsent() && requiresConsent) {
        return;
      }
      captureEvent(name, properties);
    },
    setAnalyticsConsent,
    identifyUser,
    resetUser,
    getDistinctId: () => {
      if (!hasConsent() && requiresConsent) {
        return null;
      }
      return backend.get_distinct_id?.() ?? null;
    },
    aliasDistinctId
  };
};
